import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import type { User } from '../../types';

interface UserComboboxProps {
  users: User[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
}

export function UserCombobox({
  users,
  value,
  onValueChange,
  placeholder = 'Select user...',
}: UserComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selectedUser = users.find((user) => user.email === value);

  const filteredUsers = React.useMemo(() => {
    if (!searchQuery) return users;

    const query = searchQuery.toLowerCase();
    return users.filter((user) => {
      const fullName = user.fullName?.toLowerCase() || '';
      const email = user.email.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    });
  }, [users, searchQuery]);

  // Reset highlighted index when filtered users change
  React.useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredUsers]);

  const handleSelect = (userEmail: string) => {
    onValueChange(userEmail === value ? '' : userEmail);
    setOpen(false);
    setSearchQuery('');
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredUsers.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && filteredUsers.length > 0) {
      e.preventDefault();
      handleSelect(filteredUsers[highlightedIndex].email);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearchQuery('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!open && e.target.value) {
      setOpen(true);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {selectedUser ? (
            <span className="flex items-center gap-2">
              <span className="font-medium">{selectedUser.fullName}</span>
              <span className="text-muted-foreground">({selectedUser.email})</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] p-0" align="start">
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              autoFocus
            />
          </div>
          {filteredUsers.length === 0 ? (
            <div className="py-6 text-center text-sm">
              <p className="text-muted-foreground">No user found.</p>
              {searchQuery && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            <CommandGroup className="max-h-[300px] overflow-auto">
              {filteredUsers.map((user, index) => (
                <div
                  key={user.email}
                  onClick={() => handleSelect(user.email)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    'relative flex cursor-pointer select-none items-center justify-between rounded-sm px-2 py-1.5 text-sm outline-none',
                    highlightedIndex === index
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  )}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{user.fullName}</span>
                    <span className="text-xs text-muted-foreground">{user.email}</span>
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4',
                      value === user.email ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </div>
              ))}
            </CommandGroup>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
